import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, CircleAlert, LoaderCircle, X } from 'lucide-react';
import type { AppData, LoginResponse, User } from './types';
import { api } from './services/api';
import { Layout, type PageKey } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const IntakePage = lazy(() => import('./pages/IntakePage').then(m => ({ default: m.IntakePage })));
const AssetsPage = lazy(() => import('./pages/AssetsPage').then(m => ({ default: m.AssetsPage })));
const HandoverPage = lazy(() => import('./pages/HandoverPage').then(m => ({ default: m.HandoverPage })));
const InventoryPage = lazy(() => import('./pages/InventoryPage').then(m => ({ default: m.InventoryPage })));
const AllocationPage = lazy(() => import('./pages/AllocationPage').then(m => ({ default: m.AllocationPage })));
const AreasPage = lazy(() => import('./pages/AreasPage').then(m => ({ default: m.AreasPage })));
const ReportsPage = lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })));

const validPages: PageKey[] = ['dashboard', 'intake', 'assets', 'handover', 'inventory', 'allocation', 'areas', 'reports'];
const pageFromHash = (): PageKey => {
  const value = window.location.hash.replace('#/', '') as PageKey;
  return validPages.includes(value) ? value : 'dashboard';
};

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [data, setData] = useState<AppData | null>(null);
  const [page, setPage] = useState<PageKey>(pageFromHash());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: 'success' | 'error' } | null>(null);
  const toastTimerRef = useRef<number | null>(null);

  const notify = (message: string, tone: 'success' | 'error' = 'success') => {
    setToast({ message, tone });
    if (toastTimerRef.current !== null) {
      window.clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = window.setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 4200);
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const next = await api.bootstrap();
      setData(next);
      setUser(next.currentUser);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Không thể tải dữ liệu.', 'error');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('bis-asset-control-token');
    if (token) refresh();
    else setLoading(false);
    const onHash = () => setPage(pageFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const login = async (result: LoginResponse) => {
    setUser(result.user);
    setLoading(true);
    await refresh();
  };

  const logout = async () => {
    await api.logout();
    setUser(null);
    setData(null);
    window.location.hash = '';
  };

  const navigate = (next: PageKey) => {
    setPage(next);
    window.location.hash = `/${next}`;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const content = useMemo(() => {
    if (!data) return null;
    const shared = { data, refresh, notify };
    switch (page) {
      case 'intake': return <IntakePage {...shared} />;
      case 'assets': return <AssetsPage {...shared} />;
      case 'handover': return <HandoverPage {...shared} />;
      case 'inventory': return <InventoryPage {...shared} />;
      case 'allocation': return <AllocationPage {...shared} />;
      case 'areas': return <AreasPage data={data} />;
      case 'reports': return <ReportsPage data={data} />;
      default: return <DashboardPage data={data} onNavigate={navigate} />;
    }
  }, [data, page]);

  if (loading) {
    return <div className="app-loading"><img src="/brand/bismart-logo-full.png" alt="Bi'S MART" /><LoaderCircle className="spin" /><p>Đang chuẩn bị dữ liệu Head Office...</p></div>;
  }

  if (!user || !data) return <LoginPage onLogin={login} />;

  return (
    <>
      <Layout page={page} onNavigate={navigate} user={user} onLogout={logout} onRefresh={refresh} loading={refreshing}>
        <Suspense fallback={<div className="app-loading"><LoaderCircle className="spin" /><p>Đang tải trang...</p></div>}>
          {content}
        </Suspense>
      </Layout>
      {toast && (
        <div className={`toast toast-${toast.tone}`}>
          {toast.tone === 'success' ? <CheckCircle2 size={20} /> : <CircleAlert size={20} />}
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)}><X size={17} /></button>
        </div>
      )}
    </>
  );
}
